import { useNavigate } from "react-router-dom";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useTranslation } from "react-i18next";
import Restricted from "@/consumer/restricted";
import RestrictedPage from "@/consumer/restrictedPage";
import { Tooltip, List, ListItem, ListItemText } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import LanguageIcon from "@mui/icons-material/Language";
import { useState, useRef } from "react";
import Modal from "@/components/modal";
import FileUploadInput from "@/components/forms/inputs/upload";
import GenericNotification from "@/components/notification/genericNotification";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import styles from "@/routes/admin/measurementDefinitionList.module.css";
import ExportMenu, {
  ExportMenuItem,
} from "@/components/appointmentRequest/exportMenu";
import { replaceDate } from "@/helper/dateHandling";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { apiRequest } from "@/helper/api";
import type { FileUploadRequestOptions } from "@/helper/customHooks/useFileUploadProgress";

export default function MeasurementDefinitionList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  usePageHeading(t("measurementDefinitions"));

  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState<boolean>(false);
  const [csvUploadInProgress, setCsvUploadInProgress] = useState(false);
  const [entriesModalOpen, setEntriesModalOpen] = useState<boolean>(false);
  const [entries, setEntries] = useState({
    created: [],
    updated: [],
    skipped: [],
  });
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const exportParamsRef = useRef<{
    globalFilter?: string;
    columnFilters?: any[];
    additionalUrlParameters?: any;
  }>({});

  const handleNotification = (
    variant: "success" | "error" | "warning" | "info",
    message: string,
    success: boolean
  ) => {
    setNotificationMessage(message);
    setShowNotification(success);
    setNotificationVariant(variant);
  };

  const isUsedInMeasurement = (row: any) =>
    Boolean(row.original.is_used_in_measurement);

  const navigateToFormTranslationEdit = (row: any) => {
    navigate(`/measurementDefinition/${row.original.id}/translation`);
  };

  const downloadExampleCsv = async () => {
    try {
      const response = await fetch(
        "/api/v1/measurementDefinition/example_csv/",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const result = await response.json();
        const errorContent = result
          ? result
          : t("error_downloading_example_csv");
        throw errorContent;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        t("example_measurement_definition_csv_filename") ||
        "example_measurement_definitions.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error);
      setNotificationMessage(t("download_failed"));
      setNotificationVariant("error");
      setShowNotification(true);
    }
  };

  const handleCsvUpload = async (
    files: File[],
    { onUploadProgress }: FileUploadRequestOptions,
  ) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiRequest<Record<string, any>>({
        entity: "measurementDefinition",
        additionalRouteParts: { create_from_csv: "/" },
        method: "post",
        submitData: formData,
        shouldNotImpersonate: true,
        disableGlobalSpinner: true,
        config: {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
        },
      });
      const result = response.data;

      setEntries({
        created: result["created entries"] || [],
        updated: result["updated entries"] || [],
        skipped: result["skipped entries"] || [],
      });

      setEntriesModalOpen(true);
      setNotificationMessage(t("upload_successful"));
      setNotificationVariant("success");
      setShowNotification(true);
      setRefreshTable((prev) => !prev);
    }
  };

  const refreshParentTable = () => {
    setRefreshTable((prev) => !prev);
  };

  const entriesModalContent = (
    <div className={styles.entriesModalContainer}>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>{t("created_entries")}:</p>
        <span>{entries?.created?.length || 0}</span>
      </section>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>{t("updated_entries")}:</p>
        <span>{entries?.updated?.length || 0}</span>
      </section>
      <section className={styles.entriesSectionColumn}>
        <p className={styles.incorrectEntriesText}>{t("incorrect_entries")}:</p>
        <List>
          {entries?.skipped?.map((entry: any, index: number) => (
            <ListItem key={index} className={styles.errorListItem}>
              <ListItemText
                primary={entry?.name || entry?.id || t("unknown_entry")}
                secondary={
                  entry?.fields
                    ? typeof entry.fields === "string"
                      ? entry.fields
                      : typeof entry.fields === "object" &&
                        entry.fields !== null
                      ? Object.entries(entry.fields)
                          .map(([key, value]: [string, any]) => {
                            if (Array.isArray(value)) {
                              return `${key}: ${value.join(", ")}`;
                            }
                            return `${key}: ${value}`;
                          })
                          .join("; ") || entry?.reason
                      : entry?.reason || t("unknown_reason")
                    : entry?.reason || t("unknown_reason")
                }
                secondaryTypographyProps={{ color: "red" }}
              />
            </ListItem>
          ))}
        </List>
      </section>
    </div>
  );

  const exportMenuItems: ExportMenuItem[] = [
    {
      label: "export_all_as",
      format: "csv",
    },
  ];

  const customActionButton = () => {
    return (
      <div className={styles.customActionButtonWrapper}>
        <GenericButton onClick={() => setCsvUploadModalOpen(true)}>
          {t("csv_import")}
        </GenericButton>
        <GenericButton onClick={downloadExampleCsv}>
          {t("csv_import_sample_file")}
        </GenericButton>

        <ExportMenu
          entity="measurementDefinition"
          exportParameter="export_definitions"
          menuItems={exportMenuItems}
          handleNotification={handleNotification}
          handleError={handleError}
        />
      </div>
    );
  };

  const customRowActions = (row: any) => {
    return (
      <>
        <Restricted permissionRequired="frontend_view_entity_translation">
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={t("translation_edit")}
          >
            <GenericButton
              variant="icon"
              onClick={() => navigateToFormTranslationEdit(row)}
            >
              <LanguageIcon />
            </GenericButton>
          </Tooltip>
        </Restricted>
      </>
    );
  };
  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
  };

  return (
    <RestrictedPage permissionRequired="frontend_view_measurement_definition">
      <GenericCrudTable
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        customRowActions={customRowActions}
        customTopToolBarActionButton={customActionButton()}
        entity={"measurementDefinition"}
        refreshTable={refreshTable}
        refreshTableAfterAction={refreshParentTable}
        onExportParamsChange={(params) => {
          exportParamsRef.current = params;
        }}
        allowOpenEditInNewTab
        customEditHandler={(row: any) => {
          const editUrl = `/settings/createMeasurementDefinition/${row.original.id}`;

          if (row.allowOpenInNewTab) {
            window.open(editUrl, "_blank", "noopener,noreferrer");
          } else {
            navigate(editUrl);
          }
        }}
        customCreateHandler={() => {
          navigate(`/settings/createMeasurementDefinition/`);
        }}
        disableDeleteButtonForRow={isUsedInMeasurement}
        additionalUrlParameter={{ test_for_measurement_usage: true }}
        customColumnVisibility={{ type: false, unit: false, reuse: false }}
        columns={[
          { header: t("id"), accessorKey: "id", id: "id" },
          {
            header: t("name"),
            accessorKey: "name",
            id: "name",
            enableForCreate: true,
          },
          {
            header: t("label"),
            accessorKey: "label",
            id: "label",
            enableForCreate: true,
          },
          {
            header: t("short_label"),
            accessorKey: "short_label",
            id: "short_label",
            enableForCreate: true,
          },
          {
            header: t("type"),
            accessorKey: "type",
            id: "type",
            filterVariant: "select",
            filterSelectOptions: [
              { value: "bool", label: t("bool") },
              { value: "boolNoStatement", label: t("boolNoStatement") },

              { value: "int", label: t("int") },
              {
                value: "float",
                label: t("float"),
              },
              {
                value: "choice",
                label: t("choice"),
              },
              { value: "multiselect", label: t("multiselect") },
              { value: "string", label: t("string") },
              { value: "text", label: t("text") },
              { value: "date", label: t("date") },
            ],
            enableForCreate: true,
            accessorFn: (row: any) => {
              const mapping: any = {
                bool: `${t("bool")}`,
                boolNoStatement: `${t("boolNoStatement")}`,
                multiselect: `${t("multiselect")}`,
                int: `${t("int")}`,
                float: `${t("float")}`,
                choice: `${t("choice")}`,
                string: `${t("string")}`,
                text: `${t("text")}`,
                date: `${t("date")}`,
              };

              if (mapping[row.type]) {
                return mapping[row.type];
              }

              return row.type;
            },
            selectOptions: [
              { value: "bool", label: t("bool") },
              { value: "int", label: t("int") },
              {
                value: "float",
                label: t("float"),
              },
              {
                value: "choice",
                label: t("choice"),
              },
              {
                value: "multiselect",
                label: t("multiselect"),
              },

              { value: "string", label: t("string") },
              { value: "text", label: t("text") },
              { value: "date", label: t("date") },
            ],
          },
          {
            header: t("unit"),
            accessorKey: "unit",
            id: "unit",
            enableForCreate: true,
            filterVariant: "select",
            filterSelectOptions: [
              { value: "", label: "-" },
              { value: "cm", label: t("centimeter") },
              { value: "Kg", label: t("kilogram") },
              { value: "g", label: t("gram") },
              { value: "°C", label: t("degree_celsius") },
              { value: "%", label: t("percent") },
            ],
            selectOptions: [
              { value: "", label: t("none") },
              { value: "cm", label: t("centimeter") },
              { value: "Kg", label: t("kilogram") },
              { value: "g", label: t("gram") },
              { value: "°C", label: t("degree_celcius") },
              { value: "%", label: t("percent") },
            ],
          },
          {
            header: t("reuse"),
            accessorKey: "reuse",
            id: "reuse",
            filterVariant: "select",
            filterSelectOptions: [
              {
                value: "false",
                label: t("no"),
              },
              {
                value: "true",
                label: t("yes"),
              },
            ],
            enableForCreate: true,
            accessorFn: (row: any) => {
              if (row.reuse === true) {
                return t("yes");
              }

              return t("no");
            },
            selectOptions: [
              {
                value: false,
                label: t("no"),
              },
              {
                value: true,
                label: t("yes"),
              },
            ],
          },
          {
            header: t("observation"),
            accessorKey: "observation",
            id: "observation",
            enableForCreate: true,
          },
          {
            header: t("assessment"),
            accessorKey: "assessment",
            id: "assessment",
            enableForCreate: true,
          },
          {
            header: t("dangerous_answer"),
            accessorKey: "dangerous_answer",
            id: "dangerous_answer",
            customColumnFilterId: "dangerous_answer",
            customFilterParameter: "",
            customSortingKey: "dangerous_answer",
            sortDescFirst: false,
            filterVariant: "select",
            filterSelectOptions: [
              { label: t("yes"), value: "yes" },
              { label: t("no"), value: "no" },
            ],
            enableForCreate: true,
            accessorFn: (row: any) => {
              if (row.dangerous_answer === "yes") {
                return t("yes");
              }

              return t("no");
            },
          },
          {
            header: t("possible_remedy"),
            accessorKey: "possible_remedy",
            id: "possible_remedy",
            enableForCreate: true,
          },
          {
            header: t("danger_potential"),
            accessorKey: "danger_potential",
            id: "danger_potential",
            filterVariant: "select",
            filterSelectOptions: [
              { label: t("low"), value: "low" },
              { label: t("mid"), value: "mid" },
              { label: t("high"), value: "high" },
            ],
            enableForCreate: true,
            accessorFn: (row: any) => {
              if (row.danger_potential === "low") {
                return t("low");
              } else if (row.danger_potential === "mid") {
                return t("mid");
              } else if (row.danger_potential === "high") {
                return t("high");
              } else return "";
            },
          },
          {
            header: t("measurement_is_gbu"),
            accessorKey: "is_gbu",
            id: "is_gbu",
            accessorFn: (row: any) => {
              if (row.is_gbu) {
                return t("yes");
              }

              return t("no");
            },
            filterVariant: "select",
            filterSelectOptions: [
              { label: t("yes"), value: true },
              { label: t("no"), value: false },
            ],
            enableForCreate: true,
          },
          {
            header: t("created_on"),
            accessorKey: "created_on",
            id: "created_on",
            sortingFn: "datetime",
            filterVariant: "date",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("created_by"),
            accessorKey: "created_by",
            customColumnFilterId: "created_by_name",

            id: "created_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
          {
            header: t("modified_on"),
            accessorKey: "modified_on",
            id: "modified_on",
            filterVariant: "date",

            sortingFn: "datetime",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("modified_by"),
            accessorKey: "modified_by",
            customColumnFilterId: "modified_by_name",

            id: "modified_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
        ]}
      />
      <Modal
        open={csvUploadModalOpen}
        onClose={() => setCsvUploadModalOpen(false)}
        title={t("csv_import")}
        hideDialogActions={true}
        disableCancelButton={csvUploadInProgress}
        maxWidth="40rem"
      >
        <FileUploadInput
          multiple={false}
          name="measurement_definition_csv"
          folderId="measurementDefinition"
          onCancel={() => setCsvUploadModalOpen(false)}
          customUploadHandler={handleCsvUpload}
          accept={[".csv"]}
          onUploadStateChange={setCsvUploadInProgress}
          handleUploadSuccess={(modalOpen: boolean) => {
            if (modalOpen) {
              setCsvUploadModalOpen(false);
            }
          }}
        />
      </Modal>
      <Modal
        open={entriesModalOpen}
        onSubmit={() => setEntriesModalOpen(false)}
        title={t("overview")}
        submitButtonText={t("confirm")}
        buttonPlacement="flex-end"
        hideCancelButton={true}
      >
        {entriesModalContent}
      </Modal>
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
      {errorModal}
    </RestrictedPage>
  );
}
